import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { parseCsv } from '../../utils/csv';

// What a file looks like, in the page, for every kind the vault holds.
//
// The bytes arrive as a Blob fetched with the caller's token (there is no
// URL to point anything at), so each kind is drawn from that Blob:
//   pdf          → the browser's viewer in a frame
//   image        → <img>
//   video, audio → the browser's player
//   csv, tsv     → a table — the weekly FedEx statement is a CSV
//   xlsx         → a table per sheet (read-excel-file)
//   docx         → its text and formatting (mammoth), links defused
//   text, json   → monospaced text, JSON pretty-printed
// Anything else (an old .xls or .doc, HEIC outside Safari) says so and
// offers the download. The heavy readers load only when one is opened.

export type PreviewKind = 'pdf' | 'image' | 'video' | 'audio' | 'csv' | 'tsv' | 'xlsx' | 'docx' | 'text' | 'json' | 'none';

const BY_EXTENSION: Record<string, PreviewKind> = {
  pdf: 'pdf', png: 'image', jpg: 'image', jpeg: 'image', webp: 'image', gif: 'image',
  mp4: 'video', mov: 'video', webm: 'video', m4a: 'audio', mp3: 'audio', wav: 'audio',
  csv: 'csv', tsv: 'tsv', xlsx: 'xlsx', docx: 'docx', txt: 'text', log: 'text', md: 'text', json: 'json',
};

/** The kind, from the stored type and — when that says nothing — the file's extension. */
export function previewKind(contentType = '', name = ''): PreviewKind {
  const type = contentType.toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  // HEIC is an image no browser but Safari draws; it falls to the extension
  // check below, which deliberately does not list it.
  if (type.startsWith('image/') && !type.includes('heic') && !type.includes('heif')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'text/csv') return 'csv';
  if (type === 'text/tab-separated-values') return 'tsv';
  if (type.includes('spreadsheetml')) return 'xlsx';
  if (type.includes('wordprocessingml')) return 'docx';
  if (type === 'application/json') return 'json';
  if (type.startsWith('text/')) return 'text';
  const ext = name.toLowerCase().split(/[?#]/)[0].split('.').pop() || '';
  return name.includes('.') ? BY_EXTENSION[ext] || 'none' : 'none';
}

/** Past this many rows the table stops, and says so — a big sheet must not freeze the tab. */
const MAX_ROWS = 1000;

const SheetTable: React.FC<{ rows: unknown[][] }> = ({ rows }) => {
  const [head, ...body] = rows;
  const shown = body.slice(0, MAX_ROWS);
  const width = Math.max(...rows.slice(0, MAX_ROWS + 1).map((r) => r.length), 0);
  const cell = (value: unknown) => (value instanceof Date ? value.toLocaleDateString() : value == null ? '' : String(value));
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'background.paper' }}>
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          {head && (
            <TableHead>
              <TableRow>
                {Array.from({ length: width }, (_, i) => (
                  <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{cell(head[i])}</TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {shown.map((row, r) => (
              // eslint-disable-next-line react/no-array-index-key -- rows have no identity of their own
              <TableRow key={r} hover>
                {Array.from({ length: width }, (_, i) => (
                  <TableCell key={i} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{cell(row[i])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {body.length > MAX_ROWS && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          Showing the first {MAX_ROWS.toLocaleString()} of {body.length.toLocaleString()} rows. Download it for the rest.
        </Typography>
      )}
    </Box>
  );
};

/** Links in a Word file go nowhere but the web, and never run anything. */
function defuseLinks(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('a').forEach((a) => {
    const href = a.getAttribute('href') || '';
    if (/^(https?:|mailto:)/i.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    } else a.removeAttribute('href');
  });
  doc.querySelectorAll('script, iframe, object, embed').forEach((el) => el.remove());
  return doc.body.innerHTML;
}

type Loaded =
  | { kind: 'rows'; sheets: { name: string; rows: unknown[][] }[] }
  | { kind: 'text'; text: string }
  | { kind: 'html'; html: string };

const FilePreview: React.FC<{
  blob: Blob;
  url: string;
  contentType?: string;
  /** Anything carrying the file's extension — the storage path will do. */
  name?: string;
  title?: string;
  /** How a PDF fits its frame: the page width inline, the whole page full screen. */
  fit?: 'FitH' | 'Fit';
  onDownload: () => void;
}> = ({ blob, url, contentType, name, title, fit = 'FitH', onDownload }) => {
  const kind = previewKind(contentType, name);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [sheet, setSheet] = useState(0);

  useEffect(() => {
    let active = true;
    setLoaded(null); setFailed(null); setSheet(0);
    const done = (value: Loaded) => { if (active) setLoaded(value); };
    const fail = (err: unknown) => { if (active) setFailed(err instanceof Error ? err.message : 'This file could not be read.'); };
    if (kind === 'csv' || kind === 'tsv') {
      blob.text().then((text) => done({ kind: 'rows', sheets: [{ name: '', rows: parseCsv(text, kind === 'tsv' ? '\t' : ',') }] })).catch(fail);
    } else if (kind === 'text' || kind === 'json') {
      blob.text().then((text) => {
        let shown = text;
        if (kind === 'json') { try { shown = JSON.stringify(JSON.parse(text), null, 2); } catch { /* shown as it is */ } }
        done({ kind: 'text', text: shown });
      }).catch(fail);
    } else if (kind === 'xlsx') {
      import('read-excel-file/browser')
        .then(({ default: readXlsxFile }) => readXlsxFile(blob))
        .then((sheets) => done({ kind: 'rows', sheets: sheets.map((s) => ({ name: s.sheet, rows: s.data as unknown[][] })) }))
        .catch(fail);
    } else if (kind === 'docx') {
      Promise.all([import('mammoth'), blob.arrayBuffer()])
        .then(([mammoth, arrayBuffer]) => (mammoth.default || mammoth).convertToHtml({ arrayBuffer }))
        .then((result) => done({ kind: 'html', html: defuseLinks(result.value) }))
        .catch(fail);
    }
    return () => { active = false; };
  }, [blob, kind]);

  const center = (node: React.ReactNode) => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', bgcolor: 'grey.100' }}><Box sx={{ m: 'auto', textAlign: 'center', p: 3 }}>{node}</Box></Box>
  );
  const noPreview = (why: string) => center(
    <>
      <Typography variant="body2" color="text.secondary">{why}</Typography>
      <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload} sx={{ mt: 2 }}>Download</Button>
    </>,
  );

  if (kind === 'pdf') {
    // navpanes=0 folds Chrome's thumbnail pane away; view= fits the page.
    // Viewers that do not understand them ignore them.
    return (
      <Box component="iframe" title={title} src={`${url}#toolbar=1&navpanes=0&view=${fit}`}
        sx={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
    );
  }
  if (kind === 'image') {
    return (
      <Box sx={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', bgcolor: 'grey.100' }}>
        <Box component="img" alt={title} src={url} sx={{ m: 'auto', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      </Box>
    );
  }
  if (kind === 'video') {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', bgcolor: 'common.black' }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- dashcam and phone clips carry no captions */}
        <Box component="video" src={url} controls sx={{ m: 'auto', maxWidth: '100%', maxHeight: '100%' }} />
      </Box>
    );
  }
  if (kind === 'audio') {
    // eslint-disable-next-line jsx-a11y/media-has-caption -- recordings carry no captions
    return center(<Box component="audio" src={url} controls />);
  }
  // An iPhone photo is saved as it came and turned into a JPEG a few
  // seconds later; until then only Safari could draw it.
  if (/hei[cf]/i.test(contentType || '')) return noPreview('This photo is being prepared for viewing. Open it again in a few seconds.');
  if (kind === 'none') return noPreview(`No preview for ${contentType || 'this file type'}.`);
  if (failed) return noPreview(`This file could not be shown: ${failed}`);
  if (!loaded) return center(<CircularProgress />);

  if (loaded.kind === 'text') {
    return (
      <Box component="pre" sx={{
        m: 0, p: 2, width: '100%', height: '100%', overflow: 'auto', bgcolor: 'background.paper',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}
      >
        {loaded.text}
      </Box>
    );
  }
  if (loaded.kind === 'html') {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', bgcolor: 'background.paper', p: 3, '& img': { maxWidth: '100%' }, '& table': { borderCollapse: 'collapse' }, '& td, & th': { border: '1px solid', borderColor: 'divider', p: 0.5 } }}
        // Produced by mammoth from the file, with links and embeds defused above.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: loaded.html }}
      />
    );
  }
  const { sheets } = loaded;
  if (!sheets.some((s) => s.rows.length)) return noPreview('This file is empty.');
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {sheets.length > 1 && (
        <Tabs value={sheet} onChange={(_, v) => setSheet(v)} variant="scrollable" sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          {sheets.map((s, i) => <Tab key={s.name || i} label={s.name || `Sheet ${i + 1}`} value={i} />)}
        </Tabs>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}><SheetTable rows={sheets[sheet]?.rows || []} /></Box>
    </Box>
  );
};

export const FilePreviewError: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ p: 3, width: '100%' }}><Alert severity="warning">{message}</Alert></Box>
);

export default FilePreview;
